
/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import React from 'react';
import { MoreHorizontal, Loader2, Shield, ArrowUpRight, ArrowDownLeft } from 'lucide-react';
import { Card } from '../../ui/Card';
import { Transaction, ViewState } from '../../../types';
import { TokenIcon } from '../../ui/ChainIcons';
import { MainnetValidation } from './MainnetValidation';

interface RecentTransactionsProps {
    transactions: Transaction[];
    isLoading: boolean;
    onNavigate: (view: ViewState) => void;
    tokenMap: Map<string, any>;
}

export const RecentTransactions: React.FC<RecentTransactionsProps> = ({ 
    transactions, 
    isLoading, 
    onNavigate,
    tokenMap
}) => {
    return (
        <Card noPadding className="flex-1 flex flex-col border-stone-200 dark:border-[#2D3142] overflow-hidden shadow-smooth bg-white dark:bg-[#15171C] rounded-sm min-h-[500px]">
            <div className="p-6 pb-4 flex justify-between items-center border-b border-stone-50 dark:border-white/[0.02] bg-gray-50/50 dark:bg-white/[0.01]">
                <div className="space-y-1">
                    <h3 className="font-serif text-xl font-bold text-[#0A0B0F] dark:text-white uppercase tracking-tight">Recent Activity</h3>
                    <p className="text-[10px] font-black text-stone-400 uppercase tracking-widest opacity-60">Latest Transactions</p>
                </div>
                <button onClick={() => onNavigate('transaction-history')} className="p-2.5 hover:bg-stone-100 dark:hover:bg-white/10 rounded-sm text-stone-400 transition-all">
                    <MoreHorizontal size={22} />
                </button>
            </div>
            
            <div className="flex-1 overflow-y-auto px-4 py-2 custom-scrollbar">
                {isLoading ? (
                    <div className="h-full flex items-center justify-center py-20">
                        <Loader2 className="animate-spin text-stone-200" />
                    </div>
                ) : transactions.length > 0 ? (
                    <div className="space-y-0.5">
                        {transactions.slice(0, 10).map((tx) => {
                            const isOut = ['payment', 'withdrawal', 'swap'].includes(tx.type);
                            const token = tokenMap.get(tx.asset);
                            return (
                                <div key={tx.id} className="flex items-center gap-4 p-4 rounded-sm hover:bg-stone-50 dark:hover:bg-white/[0.02] transition-all group cursor-pointer border border-transparent border-l-2 hover:border-l-nobel-gold">
                                    <div className="relative shrink-0">
                                        <div className={`w-10 h-10 rounded-sm border flex items-center justify-center shrink-0 transition-all ${isOut ? 'bg-red-500/5 border-red-500/10' : 'bg-emerald-500/5 border-emerald-500/10'}`}>
                                            {token ? <TokenIcon symbol={token.symbol} fallback={token.icon} className="w-6 h-6" /> : (isOut ? <ArrowUpRight size={18} className="text-red-400"/> : <ArrowDownLeft size={18} className="text-emerald-400"/>)}
                                        </div>
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <div className="flex justify-between items-center">
                                            <span className="text-[11px] font-black text-[#0A0B0F] dark:text-stone-300 uppercase tracking-tight truncate">{tx.type}</span>
                                            <span className={`text-[12px] font-mono font-bold ${isOut ? 'text-stone-900 dark:text-white' : 'text-emerald-500'}`}>
                                                {isOut ? '-' : '+'}${tx.usdAmount?.toFixed(2) || tx.amount.toFixed(2)}
                                            </span>
                                        </div>
                                        <div className="flex justify-between items-center mt-1">
                                            <span className="text-[9px] text-stone-400 font-mono tracking-tighter opacity-70 uppercase truncate pr-4">{tx.txHash.slice(0,24)}...</span>
                                            <span className="text-[9px] font-black text-stone-500 uppercase shrink-0">{tx.asset}</span>
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                ) : (
                    <div className="h-full flex flex-col items-center justify-center text-center py-10 opacity-40">
                        <Shield size={40} className="text-stone-200" />
                        <p className="text-[10px] font-black uppercase tracking-widest mt-4">No recent activity found</p>
                    </div>
                )}
            </div>
            
            <MainnetValidation />
        </Card>
    );
};
